import React, { useState, useRef, useMemo, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Settings,
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShieldCheck,
  BarChart3,
  Edit3,
  X,
  ListFilter,
  Brain,
  AlertTriangle,
  Calculator,
  Sparkles,
  RefreshCw,
  Package,
  Wrench,
  Users,
  Info,
  ReceiptText,
  ExternalLink,
  Rows3,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "../lib/supabase";
import { useAiModel } from "../hooks/useAiModel";
import { useSubscription } from "../hooks/useSubscription";
import type { ClaimsProject } from "../types/database.types";
import { formatCurrency, MONTH_NAMES } from "../lib/calculations";
import { RoleProtectedWrapper } from "./RoleProtectedWrapper";
import { EditExpenseCategoriesModal } from "./EditExpenseCategoriesModal";
import { PnlSettingsModal } from "./PnlSettingsModal";
import { PnlManualMetricRows } from "./pnlAnalytics/PnlManualMetricRows";
// SEQ-180 phase 2, P24: the cell editor moved out of this file so the
// dollar rows and the manual operational rows share one popover.
import { PnlEditCellModal } from "./pnlAnalytics/PnlEditCellModal";
import { PnlProjectViewModal } from "./pnlAnalytics/PnlProjectViewModal";
import { InfoTooltip } from "./InfoTooltip";
import { useAuth } from "../hooks/useAuth";
import { usePnlCategories } from "../hooks/usePnlCategories";
import { useProjectPnlView } from "../features/pnlAnalytics/useProjectPnlView";
import { resolveProjectPnlRows, valuedCategories } from "../features/pnlAnalytics/projectPnlView";
import { usePnlMonthlyData } from "../hooks/usePnlMonthlyData";
import {
  calculateMonthlyPnl,
  calculateTotalsPnl,
  getMonthRange,
  toMonthDate,
} from "../services/pnlCalculationService";
import type { MonthlyPnlResult } from "../services/pnlCalculationService";
import { logError } from "../lib/logError";
import { getPnlYearOptions } from "../lib/pnl/yearOptions";
import { orphanCategoryNames } from "../lib/pnl/orphanCategoryNames";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface ProjectStatusTabProps {
  project: ClaimsProject;
  onNavigateToClaims?: (month: number, year: number) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function shortMonth(month: number): string {
  return (MONTH_NAMES[month - 1] || "").slice(0, 3);
}


// ─────────────────────────────────────────────────────────────────────────────
// AI Insights Panel (Gemini-powered)
// ─────────────────────────────────────────────────────────────────────────────

function PnlAiInsights({
  totalsPnl,
  monthlyPnlResults,
  settings,
  projectName,
}: {
  totalsPnl: MonthlyPnlResult;
  monthlyPnlResults: { key: string; month: number; year: number; pnl: MonthlyPnlResult }[];
  settings: { materialPercent: number; wagesMultiplier: number; estimateProfit: number };
  projectName: string;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [insight, setInsight] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFormulas, setShowFormulas] = useState(false);
  const aiModel = useAiModel();
  const { logAiUsage, canUseAi } = useSubscription();

  // Pre-computed formula values for transparency
  const formulas = useMemo(() => {
    const income = totalsPnl.constructionIncome;
    const retention = totalsPnl.retention;
    const costs = totalsPnl.totalDeductions;
    const profit = totalsPnl.netProfit;
    const profitPct = totalsPnl.profitPercent;
    const wagesPct = totalsPnl.wagesPercent;
    const wages = totalsPnl.wagesWithMultiplier;
    const materials = totalsPnl.materialsBudget;
    const matPct = income > 0 ? (materials / income) * 100 : 0;

    return [
      {
        label: "Net Profit",
        formula: "Income − Retention − Total Costs",
        values: `$${income.toLocaleString("en-AU", { maximumFractionDigits: 0 })} − $${retention.toLocaleString("en-AU", { maximumFractionDigits: 0 })} − $${costs.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`,
        result: `$${profit.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`,
        status: profit >= 0 ? "success" as const : "danger" as const,
      },
      {
        label: "Profit Margin",
        formula: "Net Profit ÷ Income × 100",
        values: `$${profit.toLocaleString("en-AU", { maximumFractionDigits: 0 })} ÷ $${income.toLocaleString("en-AU", { maximumFractionDigits: 0 })} × 100`,
        result: `${profitPct.toFixed(1)}%`,
        status: (profitPct >= 10 ? "success" : profitPct >= 5 ? "warning" : "danger") as "success" | "warning" | "danger",
      },
      {
        label: "Wages Ratio",
        formula: "Total Wages ÷ Income × 100",
        values: `$${wages.toLocaleString("en-AU", { maximumFractionDigits: 0 })} ÷ $${income.toLocaleString("en-AU", { maximumFractionDigits: 0 })} × 100`,
        result: `${wagesPct.toFixed(1)}%`,
        status: (wagesPct <= 35 ? "success" : wagesPct <= 45 ? "warning" : "danger") as "success" | "warning" | "danger",
      },
      {
        label: "Materials Ratio",
        formula: "Total Materials ÷ Income × 100",
        values: `$${materials.toLocaleString("en-AU", { maximumFractionDigits: 0 })} ÷ $${income.toLocaleString("en-AU", { maximumFractionDigits: 0 })} × 100`,
        result: `${matPct.toFixed(1)}%`,
        status: (matPct <= (settings.materialPercent || 40) ? "success" : "warning") as "success" | "warning",
      },
    ];
  }, [totalsPnl, settings]);

  const generateInsight = async () => {
    const check = canUseAi("pnl_insights");
    if (!check.allowed) {
      setError(check.reason || "AI usage not available");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const monthlyBreakdown = monthlyPnlResults
        .filter((m) => m.pnl.constructionIncome > 0)
        .map((m) => {
          const shortMn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m.month - 1] || "";
          return {
            label: `${shortMn} ${m.year}`,
            income: m.pnl.constructionIncome,
            costs: m.pnl.totalDeductions,
            profit: m.pnl.netProfit,
            profitPercent: m.pnl.profitPercent,
          };
        });

      const { data, error: fnError } = await supabase.functions.invoke(
        "generate-pnl-insights",
        {
          body: {
            ai_model: aiModel,
            projectName,
            constructionIncome: totalsPnl.constructionIncome,
            retention: totalsPnl.retention,
            totalDeductions: totalsPnl.totalDeductions,
            materialsCost: totalsPnl.materialsBudget,
            wagesCost: totalsPnl.wagesWithMultiplier,
            otherCosts: totalsPnl.otherExpensesTotal,
            netProfit: totalsPnl.netProfit,
            profitPercent: totalsPnl.profitPercent,
            wagesPercent: totalsPnl.wagesPercent,
            materialPercent: settings.materialPercent,
            wagesMultiplier: settings.wagesMultiplier,
            estimateProfit: settings.estimateProfit,
            monthlyBreakdown,
          },
        },
      );

      if (fnError) throw fnError;
      if (data?.analysis) {
        setInsight(data.analysis);

        // Log AI credit usage with token data
        if (data.usage) {
          logAiUsage("pnl_insights", data.usage);
        } else {
          logAiUsage("pnl_insights");
        }
      } else {
        throw new Error("No analysis returned");
      }
    } catch (err: unknown) {
      logError("P&L Insight Error:", err);
      setError(err instanceof Error ? err.message : "Failed to generate insight");
    } finally {
      setLoading(false);
    }
  };

  if (totalsPnl.constructionIncome === 0) return null;

  const statusColors = {
    success: { bg: "#f0fdf4", border: "#86efac", text: "#15803d" },
    warning: { bg: "#fffbeb", border: "#fcd34d", text: "#a16207" },
    danger:  { bg: "#fef2f2", border: "#fca5a5", text: "#b91c1c" },
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, rgba(30,115,190,0.03) 0%, rgba(99,102,241,0.03) 100%)",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        marginBottom: 16,
        overflow: "hidden",
        animation: "fadeInUp 0.3s ease",
      }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 20px",
          background: "none",
          border: "none",
          cursor: "pointer",
          borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
        }}
      >
        <div style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", borderRadius: 8, padding: 6, display: "flex" }}>
          <Brain size={15} color="#fff" />
        </div>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", letterSpacing: "-0.01em" }}>AI Insights</span>
        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
          {insight ? "Gemini analysis" : "Generate financial analysis"}
        </span>
        <div style={{ flex: 1 }} />
        {isExpanded ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
      </button>

      {isExpanded && (
        <div style={{ padding: 16, animation: "fadeInUp 0.2s ease" }}>

          {/* Formula Transparency Section */}
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={() => setShowFormulas(!showFormulas)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 0",
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
                marginBottom: showFormulas ? 10 : 0,
              }}
            >
              <Calculator size={13} />
              <span>Key Formulas & Values</span>
              {showFormulas ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>

            {showFormulas && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 10,
                }}
              >
                {formulas.map((f, i) => {
                  const colors = statusColors[f.status];
                  return (
                    <div
                      key={f.label}
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 10,
                        padding: "10px 14px",
                        animation: `fadeInUp 0.3s ease ${i * 0.08}s both`,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.3px" }}>{f.label}</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>{f.result}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.5, fontFamily: "monospace" }}>
                        <div style={{ marginBottom: 2, fontWeight: 600 }}>{f.formula}</div>
                        <div style={{ opacity: 0.8 }}>{f.values}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Generate / Loading / Result */}
          {!insight && !loading && !error && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                padding: "24px 16px",
                background: "linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.04))",
                borderRadius: 10,
                border: "1px dashed #c7d2fe",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={18} color="#6366f1" />
                <span style={{ fontSize: 14, fontWeight: 600, color: "#3730a3" }}>
                  Generate P&L Analysis
                </span>
              </div>
              <p style={{ fontSize: 12, color: "#64748b", textAlign: "center", maxWidth: 400, margin: 0, lineHeight: 1.5 }}>
                AI will analyze your project's financial data and provide insights on profitability, cost structure, trends, and recommendations.
              </p>
              <button
                onClick={generateInsight}
                className="btn btn-primary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 24px",
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 10,
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)"; (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 16px rgba(99,102,241,0.4)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLElement).style.boxShadow = "0 2px 8px rgba(99,102,241,0.3)"; }}
              >
                <Sparkles size={14} />
                Generate Insight
              </button>
            </div>
          )}

          {loading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                padding: "32px 16px",
              }}
            >
              <Loader2 size={24} className="spinning" color="#6366f1" />
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
                Analyzing financial data with Gemini...
              </span>
            </div>
          )}

          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "14px 16px",
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: 10,
              }}
            >
              <AlertTriangle size={16} color="#ef4444" />
              <span style={{ fontSize: 12, color: "#b91c1c", flex: 1 }}>{error}</span>
              <button
                onClick={generateInsight}
                style={{
                  background: "none",
                  border: "1px solid #fca5a5",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#b91c1c",
                  cursor: "pointer",
                }}
              >
                Retry
              </button>
            </div>
          )}

          {insight && (
            <div style={{ animation: "fadeInUp 0.3s ease" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 8,
                }}
              >
                <button
                  onClick={generateInsight}
                  disabled={loading}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    background: "none",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#64748b",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#6366f1"; (e.currentTarget as HTMLElement).style.color = "#6366f1"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#e2e8f0"; (e.currentTarget as HTMLElement).style.color = "#64748b"; }}
                >
                  <RefreshCw size={12} />
                  Regenerate
                </button>
              </div>
              <div
                className="ai-markdown-content"
                style={{
                  fontSize: 13,
                  lineHeight: 1.65,
                  color: "#1e293b",
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{insight}</ReactMarkdown>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function ProjectStatusTab({ project, onNavigateToClaims }: ProjectStatusTabProps) {
  const { categories } = usePnlCategories();
  const { isAdmin, isSuperAdmin, profile } = useAuth();
  const {
    view: projectPnlView,
    loadError: projectPnlViewError,
    saving: savingProjectPnlView,
    saveView: saveProjectPnlView,
    refresh: refreshProjectPnlView,
  } = useProjectPnlView(project.id);
  const {
    incomeByMonth,
    expenseValues,
    xeroExpenseValues,
    settings,
    loading,
    updateMonthlyValue,
    updateSettings,
    refreshData,
  } = usePnlMonthlyData(project.id);

  // ── Xero comparison toggle ─────────────────────────────────────────────
  const [showXeroComparison, setShowXeroComparison] = useState(false);
  const hasXeroData = xeroExpenseValues.length > 0;

  // ── Range picker state ─────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear] = useState(currentYear);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [endYear, setEndYear] = useState(currentYear);

  // ── Modals ─────────────────────────────────────────────────────────────
  const [showSettings, setShowSettings] = useState(false);
  const [showEditCategories, setShowEditCategories] = useState(false);
  const [showProjectView, setShowProjectView] = useState(false);
  // Both halves of the mode B rule, matching what the two RPCs and the
  // archive's SELECT policy check inside the database. The role alone is not a
  // proxy for financial access in this organisation, in either direction, so
  // this pair is the client's copy of the database's rule and never a
  // simplification of it.
  const canClearPnlValues = Boolean((isAdmin || isSuperAdmin) && profile?.can_view_financials);

  // A clear or a restore moves value rows AND the project's overlay, both
  // inside one transaction on the server, so both have to be re-read here. The
  // tab is the only holder of either.
  const handleClearedOrRestored = useCallback(() => {
    void refreshData();
    void refreshProjectPnlView();
  }, [refreshData, refreshProjectPnlView]);
  // Bumped when the category editor adds or deletes an operational metric.
  // The band below reads the same registry and is mounted behind that
  // dialog, so it is keyed on this and refetches rather than showing a row
  // for a metric that no longer exists.
  const [operationalMetricsToken, setOperationalMetricsToken] = useState(0);
  const [editCell, setEditCell] = useState<{
    category: string;
    month: number;
    year: number;
    value: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  // ── Section collapse & guide ───────────────────────────────────────────
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [showGuide, setShowGuide] = useState(() => {
    try { return localStorage.getItem('pnl-guide-dismissed') !== 'true'; } catch { return true; }
  });

  const toggleSection = useCallback((key: string) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const dismissGuide = useCallback(() => {
    setShowGuide(false);
    try { localStorage.setItem('pnl-guide-dismissed', 'true'); } catch { /* storage unavailable */ }
  }, []);

  // ── Scroll ─────────────────────────────────────────────────────────────
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollLeft = () => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollLeft -= 200;
  };
  const scrollRight = () => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollLeft += 200;
  };

  // ── Month columns ─────────────────────────────────────────────────────
  const monthColumns = useMemo(
    () => getMonthRange(startMonth, startYear, endMonth, endYear),
    [startMonth, startYear, endMonth, endYear],
  );

  // ── Build expense lookup ──────────────────────────────────────────────
  const expenseLookup = useMemo(() => {
    const map = new Map<string, number>();
    for (const ev of expenseValues) {
      // Key: "category|YYYY-MM"
      const monthKey = ev.monthDate.substring(0, 7); // "YYYY-MM"
      map.set(`${ev.category}|${monthKey}`, ev.amount);
    }
    return map;
  }, [expenseValues]);

  const getExpenseValue = useCallback(
    (category: string, monthKey: string): number => {
      return expenseLookup.get(`${category}|${monthKey}`) || 0;
    },
    [expenseLookup],
  );

  // ── Build Xero expense lookup ──────────────────────────────────────────
  const xeroExpenseLookup = useMemo(() => {
    const map = new Map<string, number>();
    for (const ev of xeroExpenseValues) {
      const monthKey = ev.monthDate.substring(0, 7);
      map.set(`${ev.category}|${monthKey}`, ev.amount);
    }
    return map;
  }, [xeroExpenseValues]);

  const getXeroExpenseValue = useCallback(
    (category: string, monthKey: string): number => {
      return xeroExpenseLookup.get(`${category}|${monthKey}`) || 0;
    },
    [xeroExpenseLookup],
  );

  // Typed values in a category that is no longer in the organisation list
  // still count, as the analytics page and the dashboard count them (SEQ-240).
  const orphanNames = useMemo(() => orphanCategoryNames(categories, expenseValues), [categories, expenseValues]);

  // ── Calculate all monthly P&L ─────────────────────────────────────────
  const monthlyPnlResults = useMemo(() => {
    const results: { key: string; month: number; year: number; pnl: MonthlyPnlResult }[] = [];
    for (const mc of monthColumns) {
      const incomeData = incomeByMonth.get(mc.key);
      const income = incomeData?.income || 0;
      const retention = incomeData?.retention || 0;

      // Build expenses for this month
      const manualExpenses: Record<string, number> = {};
      for (const cat of categories) {
        manualExpenses[cat.name] = getExpenseValue(cat.name, mc.key);
      }
      for (const name of orphanNames) {
        manualExpenses[name] = getExpenseValue(name, mc.key);
      }

      const pnl = calculateMonthlyPnl(income, retention, manualExpenses, categories, settings);
      results.push({ key: mc.key, month: mc.month, year: mc.year, pnl });
    }
    return results;
  }, [monthColumns, incomeByMonth, categories, orphanNames, settings, getExpenseValue]);

  // ── Totals ─────────────────────────────────────────────────────────────
  const totalsPnl = useMemo(
    () => calculateTotalsPnl(monthlyPnlResults.map((r) => r.pnl), categories, settings),
    [monthlyPnlResults, categories, settings],
  );

  // ── The project's own view of the organisation category list ───────────
  //
  // The overlay decides what this project RENDERS. It never decides what is
  // computed: `categories` above is handed to the engine untouched, and the
  // resolver hands back the same list as computeCategories for the group
  // subtotals, so a hidden row can never take money out of a total. A category
  // that is hidden and holds a value renders anyway, with a chip, because the
  // company dashboard is still counting it.
  const valuedOnThisProject = useMemo(
    () => valuedCategories([...expenseValues, ...xeroExpenseValues]),
    [expenseValues, xeroExpenseValues],
  );

  const projectView = useMemo(
    () => resolveProjectPnlRows({ orgCategories: categories, view: projectPnlView, valued: valuedOnThisProject }),
    [categories, projectPnlView, valuedOnThisProject],
  );

  // ── Group the project's rows by section ────────────────────────────────
  const rowsByGroup = useMemo(() => {
    const groups: Record<string, typeof projectView.rows> = { Materials: [], Wages: [], Other: [] };
    for (const row of projectView.rows) {
      const g = row.group || "Other";
      if (!groups[g]) groups[g] = [];
      groups[g].push(row);
    }
    return groups;
  }, [projectView]);

  // ── Handle cell save ───────────────────────────────────────────────────
  // `value` is `number | null` because PnlEditCellModal serves the manual
  // operational rows too, where a blank cell means "no value". The dollar
  // rows do not pass `allowClear`, so null never reaches here; the ?? 0
  // keeps the old `parseFloat(value) || 0` behaviour explicit rather than
  // relying on that.
  const handleSaveCell = async (value: number | null) => {
    if (!editCell) return;
    setSaving(true);
    const monthDate = toMonthDate(editCell.month, editCell.year);
    await updateMonthlyValue(editCell.category, monthDate, value ?? 0);
    setSaving(false);
    setEditCell(null);
  };

  // ── Year preset ────────────────────────────────────────────────────────
  const setYearPreset = (year: number) => {
    setStartMonth(1);
    setStartYear(year);
    setEndMonth(12);
    setEndYear(year);
  };

  // ── Layout constants ───────────────────────────────────────────────────
  const COL_LABEL = 200;
  const COL_TOTAL = 110;
  const COL_MONTH = 110;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "120px 0",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Loader2 size={36} className="spinner" color="var(--color-primary)" />
        <span style={{ color: "var(--text-muted)", fontSize: 14 }}>
          Loading P&L data…
        </span>
      </div>
    );
  }

  // ── Render helper: format cell value ───────────────────────────────────
  const fmtCell = (v: number, prefix = "$") => {
    if (v === 0) return <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>—</span>;
    const isNeg = v < 0;
    const formatted = `${prefix}${Math.abs(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
      <span style={{ color: isNeg ? "var(--color-error)" : "var(--text-primary)" }}>
        {isNeg ? `(${formatted})` : formatted}
      </span>
    );
  };

  const pctCell = (v: number) => {
    if (v === 0) return <span style={{ color: "var(--text-muted)" }}>0.00%</span>;
    const isNeg = v < 0;
    return (
      <span style={{ color: isNeg ? "var(--color-error)" : v > 20 ? "var(--color-success)" : "var(--text-primary)", fontWeight: 600 }}>
        {v.toFixed(2)}%
      </span>
    );
  };

  // ── Table cell styles ──────────────────────────────────────────────────
  const cellSz = (width: number): React.CSSProperties => ({
    width, minWidth: width, maxWidth: width,
    padding: "8px 12px",
    textAlign: "right",
    fontSize: 12.5,
    whiteSpace: "nowrap",
  });

  return (
    <>
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
                Profit & Loss
              </h2>
              <InfoTooltip text="This dashboard shows your monthly financial breakdown. Click the pencil icon on any expense cell to edit values. Use the year buttons or date range controls to filter by period." />
            </div>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
              Monthly financial breakdown. Materials {settings.materialPercent}% · Wages ×{settings.wagesMultiplier.toFixed(2)}
            </p>
          </div>
        </div>
        <RoleProtectedWrapper>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowEditCategories(true)}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <ListFilter size={14} /> Categories
            </button>
            {/* Beside Categories on purpose. The organisation list is edited
                there and printed here, so the control that says "this line
                does not apply to this project" belongs next to the one that
                creates the line, not on an admin page nobody visits. */}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowProjectView(true)}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              title="Choose which of the organisation's expense rows this project shows, in what order, under what name"
            >
              <Rows3 size={14} />
              Rows
              {projectView.hiddenCount > 0 && (
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    lineHeight: 1,
                    padding: "3px 6px",
                    borderRadius: 20,
                    background: "var(--bg-tertiary)",
                    color: "var(--seq-blue-dark)",
                  }}
                >
                  {projectView.rows.length}/{projectView.computeCategories.length}
                </span>
              )}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowSettings(true)}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Settings size={14} /> Settings
            </button>
            {hasXeroData && (
              <button
                className={`btn btn-sm ${showXeroComparison ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setShowXeroComparison(prev => !prev)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  ...(showXeroComparison ? {
                    background: 'linear-gradient(135deg, #13B5EA 0%, #0078C1 100%)',
                    border: 'none', color: '#fff',
                  } : {}),
                }}
                title="Toggle Xero data comparison"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
                {showXeroComparison ? 'Hide Xero' : 'Show Xero'}
              </button>
            )}
          </div>
        </RoleProtectedWrapper>
      </div>

      {/* ─── KPI Strip ──────────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {[
          {
            label: "Construction Income",
            value: formatCurrency(totalsPnl.constructionIncome),
            icon: <DollarSign size={18} />,
            color: "#10b981",
            tooltip: "Total income from progress claims for the selected period.",
          },
          {
            label: "Retention held in period",
            value: formatCurrency(totalsPnl.retention),
            icon: <ShieldCheck size={18} />,
            color: "#f59e0b",
            tooltip: "Retention withheld from the claims in the selected period, before any is paid back. Not deducted from profit. The dashboard card Held less received is all time and takes off what has been paid back.",
          },
          {
            label: "Net Profit",
            value: formatCurrency(totalsPnl.netProfit),
            icon: totalsPnl.netProfit >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />,
            color: totalsPnl.netProfit >= 0 ? "#16a34a" : "#dc2626",
            subtitle: `${totalsPnl.profitPercent.toFixed(1)}% margin`,
            tooltip: "Income minus all deductions (materials budget, labor, overheads).",
          },
          {
            label: "Est. Profit Target",
            value: formatCurrency(settings.estimateProfit),
            icon: <BarChart3 size={18} />,
            color: "#6366f1",
            subtitle: settings.estimateProfit > 0
              ? `${((totalsPnl.netProfit / settings.estimateProfit) * 100).toFixed(0)}% achieved`
              : "Not set",
            tooltip: "Your target profit amount. Set this in P&L Settings.",
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "16px 20px",
              border: "1px solid #e2e8f0",
              borderLeft: `4px solid ${kpi.color}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              transition: "box-shadow 0.2s, transform 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
              (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
              (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <div style={{ color: kpi.color, opacity: 0.8 }}>{kpi.icon}</div>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {kpi.label}
              </span>
              <InfoTooltip text={kpi.tooltip} />
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
              {kpi.value}
            </div>
            {kpi.subtitle && (
              <div style={{ fontSize: 11, color: kpi.color, marginTop: 4, fontWeight: 500 }}>
                {kpi.subtitle}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ─── AI Insights Panel ─────────────────────────────────────────── */}
      <PnlAiInsights
        totalsPnl={totalsPnl}
        monthlyPnlResults={monthlyPnlResults}
        settings={settings}
        projectName={project.job_location || "Project"}
      />

      {/* ─── Quick Guide Banner ────────────────────────────────────────── */}
      {showGuide && (
        <div className="pnl-guide">
          <div className="pnl-guide-icon">
            <Info size={18} />
          </div>
          <div className="pnl-guide-tips">
            <div className="pnl-guide-tip">
              <Edit3 size={13} color="var(--color-primary)" />
              <span><strong>Edit values</strong>: click any expense cell to update amounts</span>
            </div>
            <div className="pnl-guide-tip">
              <ListFilter size={13} color="var(--color-primary)" />
              <span><strong>Manage categories</strong>: use the Categories button to add or remove expense types</span>
            </div>
            <div className="pnl-guide-tip">
              <Settings size={13} color="var(--color-primary)" />
              <span><strong>Configure</strong>: set materials %, wages multiplier and profit targets in Settings</span>
            </div>
            <div className="pnl-guide-tip">
              <ChevronDown size={13} color="var(--color-primary)" />
              <span><strong>Collapse sections</strong>: click section headers to expand or collapse groups</span>
            </div>
          </div>
          <button className="pnl-guide-close" onClick={dismissGuide} aria-label="Dismiss guide">
            <X size={16} />
          </button>
        </div>
      )}

      {/* ─── Range Controls ─────────────────────────────────────────────── */}
      <div className="pnl-range-bar">
        <div className="pnl-year-tabs">
          {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((year) => (
            <button
              key={year}
              type="button"
              className={`pnl-year-tab ${
                startYear === year && endYear === year && startMonth === 1 && endMonth === 12
                  ? "active"
                  : ""
              }`}
              onClick={() => setYearPreset(year)}
            >
              {year}
            </button>
          ))}
        </div>
        <div className="pnl-divider" />
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <select value={startMonth} onChange={(e) => setStartMonth(parseInt(e.target.value))}>
            {MONTH_NAMES.map((name, i) => (
              <option key={i} value={i + 1}>{name.slice(0, 3)}</option>
            ))}
          </select>
          <select style={{ width: 82 }} value={startYear} onChange={(e) => setStartYear(parseInt(e.target.value))}>
            {getPnlYearOptions(currentYear).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <span style={{ color: "var(--text-muted)", fontSize: 13 }}>→</span>
          <select value={endMonth} onChange={(e) => setEndMonth(parseInt(e.target.value))}>
            {MONTH_NAMES.map((name, i) => (
              <option key={i} value={i + 1}>{name.slice(0, 3)}</option>
            ))}
          </select>
          <select style={{ width: 82 }} value={endYear} onChange={(e) => setEndYear(parseInt(e.target.value))}>
            {getPnlYearOptions(currentYear).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="pnl-scroll-controls">
          <button type="button" onClick={scrollLeft} aria-label="Scroll left">
            <ChevronLeft size={16} />
          </button>
          <span className="pnl-scroll-badge">{monthColumns.length} months</span>
          <button type="button" onClick={scrollRight} aria-label="Scroll right">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* ─── Spreadsheet Table ──────────────────────────────────────────── */}
      <div ref={scrollContainerRef} className="pnl-table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ ...cellSz(COL_LABEL), position: "sticky", left: 0, zIndex: 20, textAlign: "left" }}>Category</th>
              <th style={{ ...cellSz(COL_TOTAL), position: "sticky", left: COL_LABEL, zIndex: 20 }}>Totals</th>
              {monthColumns.map((mc) => (
                <th key={mc.key} style={cellSz(COL_MONTH)}>
                  {shortMonth(mc.month)}-{String(mc.year).slice(2)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>

            {/* ── REVENUE SECTION ─────────────────────────────────────── */}
            <tr
              className="pnl-section-header pnl-section-revenue"
              onClick={() => toggleSection("revenue")}
            >
              <td colSpan={2} className="pnl-section-header-sticky">
                <div className="pnl-section-header-inner">
                  <div className="pnl-section-badge"><DollarSign size={14} /></div>
                  Revenue
                  <span data-seq-private="" style={{ fontWeight: 400, fontSize: 11, opacity: 0.7, marginLeft: 4 }}>
                    {formatCurrency(totalsPnl.constructionIncome)}
                  </span>
                  <div className="pnl-section-chevron">
                    {collapsedSections.revenue ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>
              </td>
              <td colSpan={monthColumns.length} className="pnl-section-header-fill" />
            </tr>

            {!collapsedSections.revenue && (
              <>
                {/* Construction Income */}
                <tr className="pnl-data-row">
                  <td className="pnl-cat-label pnl-sticky-label" style={{ ...cellSz(COL_LABEL), fontWeight: 600, color: "#065f46" }}>
                    Construction Income
                    <InfoTooltip text="This value is sourced from your Claims History. Click any monthly cell to view the corresponding claim." />
                  </td>
                  <td className="pnl-sticky-total" style={{ ...cellSz(COL_TOTAL), position: "sticky", left: COL_LABEL, fontWeight: 700 }}>
                    {fmtCell(totalsPnl.constructionIncome)}
                  </td>
                  {monthlyPnlResults.map((mr) => (
                    <td
                      key={mr.key}
                      className={onNavigateToClaims && mr.pnl.constructionIncome !== 0 ? "pnl-claims-link" : ""}
                      style={cellSz(COL_MONTH)}
                      onClick={() => {
                        if (onNavigateToClaims && mr.pnl.constructionIncome !== 0) {
                          onNavigateToClaims(mr.month, mr.year);
                        }
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                        {fmtCell(mr.pnl.constructionIncome)}
                        {onNavigateToClaims && mr.pnl.constructionIncome !== 0 && (
                          <ExternalLink size={10} className="pnl-link-icon" style={{ flexShrink: 0, color: "var(--color-primary)" }} />
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
                {/* Retention */}
                <tr className="pnl-data-row">
                  <td className="pnl-cat-label pnl-sticky-label" style={{ ...cellSz(COL_LABEL), fontWeight: 600, color: "#92400e" }}>
                    Retention
                    <InfoTooltip text="Retention withheld from progress claims. This value is sourced from Claims History. To adjust, update the retention in the Claims tab." />
                  </td>
                  <td className="pnl-sticky-total" style={{ ...cellSz(COL_TOTAL), position: "sticky", left: COL_LABEL, fontWeight: 700 }}>
                    {fmtCell(totalsPnl.retention)}
                  </td>
                  {monthlyPnlResults.map((mr) => (
                    <td
                      key={mr.key}
                      className={onNavigateToClaims && mr.pnl.retention !== 0 ? "pnl-claims-link" : ""}
                      style={cellSz(COL_MONTH)}
                      onClick={() => {
                        if (onNavigateToClaims && mr.pnl.retention !== 0) {
                          onNavigateToClaims(mr.month, mr.year);
                        }
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                        {fmtCell(mr.pnl.retention)}
                        {onNavigateToClaims && mr.pnl.retention !== 0 && (
                          <ExternalLink size={10} className="pnl-link-icon" style={{ flexShrink: 0, color: "var(--color-primary)" }} />
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* ── MATERIALS BUDGET SECTION ────────────────────────────── */}
            <tr
              className="pnl-section-header pnl-section-materials-budget"
              onClick={() => toggleSection("materialsBudget")}
            >
              <td colSpan={2} className="pnl-section-header-sticky">
                <div className="pnl-section-header-inner">
                  <div className="pnl-section-badge"><Package size={14} /></div>
                  Materials Budget
                  <span data-seq-private="" style={{ fontWeight: 400, fontSize: 11, opacity: 0.7, marginLeft: 4 }}>
                    {formatCurrency(totalsPnl.materialsBudget)} ({settings.materialPercent}% of income)
                  </span>
                  <InfoTooltip text={`Auto-calculated as Construction Income × ${settings.materialPercent}%. Adjust in Settings.`} />
                  <div className="pnl-section-chevron">
                    {collapsedSections.materialsBudget ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>
              </td>
              <td colSpan={monthColumns.length} className="pnl-section-header-fill" />
            </tr>

            {!collapsedSections.materialsBudget && (
              <tr className="pnl-data-row">
                <td className="pnl-cat-label pnl-sticky-label" style={{ ...cellSz(COL_LABEL), color: "#6d28d9" }}>
                  Materials {settings.materialPercent}%
                </td>
                <td className="pnl-sticky-total" style={{ ...cellSz(COL_TOTAL), position: "sticky", left: COL_LABEL, fontWeight: 700 }}>
                  {fmtCell(totalsPnl.materialsBudget)}
                </td>
                {monthlyPnlResults.map((mr) => (
                  <td key={mr.key} style={cellSz(COL_MONTH)}>{fmtCell(mr.pnl.materialsBudget)}</td>
                ))}
              </tr>
            )}

            {/* ── EXPENSE CATEGORIES BY GROUP ────────────────────── */}
            {(["Materials", "Other", "Wages"] as const).map((groupName) => {
              const groupRows = rowsByGroup[groupName] || [];
              const groupCats = projectView.computeCategories.filter((c) => (c.group || "Other") === groupName);
              if (groupCats.length === 0) return null;

              const sectionKey = `expenses-${groupName.toLowerCase()}`;
              const isCollapsed = collapsedSections[sectionKey];

              // Section config
              const sectionConfig: Record<string, { icon: React.ReactNode; css: string }> = {
                Materials: { icon: <Package size={14} />, css: "pnl-section-expenses-materials" },
                Other:     { icon: <Wrench size={14} />,  css: "pnl-section-expenses-other" },
                Wages:     { icon: <Users size={14} />,   css: "pnl-section-expenses-wages" },
              };
              const conf = sectionConfig[groupName] || sectionConfig.Other;

              // Group total over the organisation's categories for this group,
              // never over the rendered subset: the two are equal whenever a
              // hidden category is empty, and when one is not, this is the
              // figure that still agrees with the company dashboard.
              const groupTotal = groupCats.reduce((sum, cat) => sum + (totalsPnl.expensesByCategory[cat.name] || 0), 0);
              const hiddenInGroup = groupCats.length - groupRows.length;

              return (
                <React.Fragment key={groupName}>
                  {/* Section header */}
                  <tr
                    className={`pnl-section-header ${conf.css}`}
                    onClick={() => toggleSection(sectionKey)}
                  >
                    <td colSpan={2} className="pnl-section-header-sticky">
                      <div className="pnl-section-header-inner">
                        <div className="pnl-section-badge">{conf.icon}</div>
                        {groupName} Expenses
                        <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.7, marginLeft: 4 }}>
                          {hiddenInGroup > 0
                            ? `${groupRows.length} of ${groupCats.length} shown`
                            : `${groupCats.length} ${groupCats.length === 1 ? "category" : "categories"}`}
                          {" · "}
                          {formatCurrency(groupTotal)}
                        </span>
                        {groupName === "Wages" && (
                          <InfoTooltip text={`Wages total is multiplied by ×${settings.wagesMultiplier.toFixed(2)}. Applied to the aggregate total only.`} />
                        )}
                        <div className="pnl-section-chevron">
                          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                        </div>
                      </div>
                    </td>
                    <td colSpan={monthColumns.length} className="pnl-section-header-fill" />
                  </tr>

                  {/* Category rows */}
                  {!isCollapsed && groupRows.map((row) => (
                    <React.Fragment key={`cat-${row.category}`}>
                    <tr className="pnl-cat-row">
                      <td
                        className="pnl-cat-label pnl-sticky-label"
                        style={{
                          ...cellSz(COL_LABEL),
                          color: row.category === "Wages" ? "#92400e" : row.category === "Patching" ? "#dc2626" : undefined,
                          fontWeight: row.category === "Wages" ? 700 : 500,
                        }}
                      >
                        {row.label}
                        {row.isRelabelled && (
                          // The stored category string is what every write,
                          // every figure and claims_project_dashboard's
                          // Patching arm still key on, so it stays on screen.
                          <span style={{ display: "block", fontSize: 10, fontWeight: 400, color: "var(--text-muted)" }}>
                            {row.category}
                          </span>
                        )}
                        {row.driftVisible && (
                          // Hidden, and holding a value again: the Xero sync,
                          // a concurrent session or a restore put money back
                          // under a category this project had hidden. The row
                          // returns rather than the money disappearing.
                          <span
                            style={{
                              display: "inline-block", marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                              fontSize: 10, fontWeight: 500, color: "#92400e", background: "#fef3c7",
                            }}
                            title="This category is hidden on this project but holds values again, so it is shown here and counted in the totals."
                          >
                            hidden, has values
                          </span>
                        )}
                        {row.category === "Wages" && (
                          <span style={{ fontSize: 10, color: "#f59e0b", marginLeft: 6, fontWeight: 400 }}>
                            ×{settings.wagesMultiplier.toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td
                        className="pnl-sticky-total"
                        style={{
                          ...cellSz(COL_TOTAL),
                          position: "sticky",
                          left: COL_LABEL,
                          fontWeight: row.category === "Wages" ? 700 : 600,
                        }}
                      >
                        {fmtCell(totalsPnl.expensesByCategory[row.category] || 0)}
                      </td>
                      {monthlyPnlResults.map((mr) => {
                        const val = mr.pnl.expensesByCategory[row.category] || 0;
                        return (
                          <td
                            key={mr.key}
                            className="pnl-edit-cell"
                            style={cellSz(COL_MONTH)}
                            // SEQ-180 phase 2 (React review, MEDIUM): a cell
                            // that only responds to a click cannot be opened
                            // by a keyboard user at all. Fixed here and on
                            // the operational band's manual cells together,
                            // so the two editable bands behave the same way.
                            role="button"
                            tabIndex={0}
                            aria-label={`Edit ${row.label} for ${mr.month}/${mr.year}`}
                            onClick={() =>
                              setEditCell({
                                category: row.category,
                                month: mr.month,
                                year: mr.year,
                                value: val,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setEditCell({
                                  category: row.category,
                                  month: mr.month,
                                  year: mr.year,
                                  value: val,
                                });
                              }
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                              {fmtCell(val)}
                              <Edit3 size={10} className="pnl-edit-icon" style={{ flexShrink: 0, color: "var(--color-primary)" }} />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {/* Xero comparison sub-row */}
                    {showXeroComparison && (
                      <tr key={`${row.category}-xero`} style={{
                        background: 'rgba(19,181,234,0.04)',
                        borderBottom: '1px solid rgba(19,181,234,0.08)',
                      }}>
                        <td
                          className="pnl-cat-label pnl-sticky-label"
                          style={{
                            ...cellSz(COL_LABEL),
                            fontSize: 11,
                            color: '#0078C1',
                            fontWeight: 500,
                            paddingLeft: 24,
                          }}
                        >
                          ↳ Xero
                        </td>
                        <td
                          className="pnl-sticky-total"
                          style={{
                            ...cellSz(COL_TOTAL),
                            position: "sticky",
                            left: COL_LABEL,
                            fontSize: 11,
                            color: '#0078C1',
                          }}
                        >
                          {(() => {
                            const xeroTotal = monthlyPnlResults.reduce((sum, mr) =>
                              sum + getXeroExpenseValue(row.category, mr.key), 0);
                            return fmtCell(xeroTotal);
                          })()}
                        </td>
                        {monthlyPnlResults.map((mr) => {
                          const xeroVal = getXeroExpenseValue(row.category, mr.key);
                          const manualVal = mr.pnl.expensesByCategory[row.category] || 0;
                          const diff = Math.abs(xeroVal - manualVal);
                          const isMatch = diff < 0.01;
                          return (
                            <td
                              key={mr.key}
                              style={{
                                ...cellSz(COL_MONTH),
                                fontSize: 11,
                                color: xeroVal === 0 ? 'var(--text-muted)' : isMatch ? '#10b981' : '#f59e0b',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                                {fmtCell(xeroVal)}
                                {!isMatch && xeroVal !== 0 && manualVal !== 0 && (
                                  <span data-seq-private="" style={{ fontSize: 9, color: '#ef4444' }} title={`Diff: $${diff.toFixed(2)}`}>
                                    ≠
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    )}
                    </React.Fragment>
                  ))}

                  {/* Design audit, MEDIUM: the band sits under Wages, but it
                      is not wages. Without a label its rows read as more
                      Wages categories that mysteriously do not add up to the
                      section total. One heading covers both halves, the
                      hours rows and the organisation's own metrics, and
                      says plainly that nothing in it is money. */}
                  {!isCollapsed && groupName === "Wages" && (
                    <tr className="pnl-operational-band-heading">
                      <th
                        scope="colgroup"
                        colSpan={2 + monthlyPnlResults.length}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px 4px",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Operational metrics
                        <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, marginLeft: 8 }}>
                          Counts and hours, not dollars. Hours come from payroll, anything typed overrides them, and none of it is part of a P&amp;L total.
                        </span>
                      </th>
                    </tr>
                  )}

                  {/* SEQ-180 phase 2, P24, and the 17 September 2026 manual
                      hours change: the whole operational band, editable.
                      PnlOperationalRows used to render Ordinary Hours,
                      Overtime Hours and Total Labour Hours read-only above
                      this from pnl_analytics_query; hours are typed by hand
                      now, so those rows come through this component and the
                      read-only one was removed rather than repeating the
                      same two numbers. Same mount point and the same reason
                      it is safe: it is outside rowsByGroup, so the Wages
                      section total above cannot see it. The band is mounted
                      once, in Wages, because a custom metric's registry
                      section is only 'labour' or 'expense' and cannot tell
                      Materials from Other. */}
                  {!isCollapsed && groupName === "Wages" && (
                    <PnlManualMetricRows
                      key={`manual-metrics-${operationalMetricsToken}`}
                      projectId={project.id}
                      monthColumns={monthlyPnlResults.map((mr) => ({ key: mr.key, month: mr.month, year: mr.year }))}
                      colLabelWidth={COL_LABEL}
                      colTotalWidth={COL_TOTAL}
                      colMonthWidth={COL_MONTH}
                    />
                  )}
                </React.Fragment>
              );
            })}

            {/* ── SUMMARY SECTION ───────────────────────────────────── */}
            <tr
              className="pnl-section-header pnl-section-summary"
              onClick={() => toggleSection("summary")}
            >
              <td colSpan={2} className="pnl-section-header-sticky">
                <div className="pnl-section-header-inner">
                  <div className="pnl-section-badge"><ReceiptText size={14} /></div>
                  Summary & Ratios
                  <span data-seq-private="" style={{ fontWeight: 400, fontSize: 11, opacity: 0.7, marginLeft: 4 }}>
                    Net Profit: {formatCurrency(totalsPnl.netProfit)} ({totalsPnl.profitPercent.toFixed(1)}%)
                  </span>
                  <div className="pnl-section-chevron">
                    {collapsedSections.summary ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>
              </td>
              <td colSpan={monthColumns.length} className="pnl-section-header-fill" />
            </tr>

            {!collapsedSections.summary && (
              <>
                {/* Net Profit */}
                <tr className="pnl-profit-row">
                  <td
                    className="pnl-sticky-label"
                    style={{
                      ...cellSz(COL_LABEL),
                      textAlign: "left",
                      paddingLeft: 44,
                      fontWeight: 700,
                      fontSize: 13,
                      color: totalsPnl.netProfit >= 0 ? "var(--color-success)" : "var(--color-error)",
                      background: totalsPnl.netProfit >= 0 ? "#ecfdf5" : "#fef2f2",
                    }}
                  >
                    Net Profit / (Loss)
                  </td>
                  <td
                    className="pnl-sticky-total"
                    style={{
                      ...cellSz(COL_TOTAL),
                      position: "sticky",
                      left: COL_LABEL,
                      fontWeight: 700,
                      fontSize: 13,
                      background: totalsPnl.netProfit >= 0 ? "#ecfdf5" : "#fef2f2",
                    }}
                  >
                    {fmtCell(totalsPnl.netProfit)}
                  </td>
                  {monthlyPnlResults.map((mr) => (
                    <td
                      key={mr.key}
                      style={{
                        ...cellSz(COL_MONTH),
                        fontWeight: 700,
                        fontSize: 13,
                        background: mr.pnl.netProfit >= 0 ? "#f0fdf4" : "#fef2f2",
                      }}
                    >
                      {fmtCell(mr.pnl.netProfit)}
                    </td>
                  ))}
                </tr>

                {/* Profit % */}
                <tr className="pnl-pct-row pnl-summary-row">
                  <td className="pnl-cat-label pnl-sticky-label" style={{ ...cellSz(COL_LABEL), fontWeight: 600, color: "#ec4899" }}>Profit %</td>
                  <td className="pnl-sticky-total" style={{ ...cellSz(COL_TOTAL), position: "sticky", left: COL_LABEL, fontWeight: 600 }}>{pctCell(totalsPnl.profitPercent)}</td>
                  {monthlyPnlResults.map((mr) => (
                    <td key={mr.key} style={cellSz(COL_MONTH)}>{pctCell(mr.pnl.profitPercent)}</td>
                  ))}
                </tr>

                {/* Wages % */}
                <tr className="pnl-pct-row pnl-summary-row">
                  <td className="pnl-cat-label pnl-sticky-label" style={{ ...cellSz(COL_LABEL), color: "#92400e" }}>Wages %</td>
                  <td className="pnl-sticky-total" style={{ ...cellSz(COL_TOTAL), position: "sticky", left: COL_LABEL }}>{pctCell(totalsPnl.wagesPercent)}</td>
                  {monthlyPnlResults.map((mr) => (
                    <td key={mr.key} style={cellSz(COL_MONTH)}>{pctCell(mr.pnl.wagesPercent)}</td>
                  ))}
                </tr>

                {/* Materials % */}
                <tr className="pnl-pct-row pnl-summary-row">
                  <td className="pnl-cat-label pnl-sticky-label" style={{ ...cellSz(COL_LABEL), color: "#6d28d9" }}>Materials %</td>
                  <td className="pnl-sticky-total" style={{ ...cellSz(COL_TOTAL), position: "sticky", left: COL_LABEL }}>{pctCell(totalsPnl.materialsPercent)}</td>
                  {monthlyPnlResults.map((mr) => (
                    <td key={mr.key} style={cellSz(COL_MONTH)}>{pctCell(mr.pnl.materialsPercent)}</td>
                  ))}
                </tr>
              </>
            )}

          </tbody>
        </table>
      </div>

      {/* ─── Modals ─────────────────────────────────────────────────────── */}
      <PnlSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSave={updateSettings}
      />

      <EditExpenseCategoriesModal
        key={showEditCategories ? 'categories-open' : 'categories-closed'}
        isOpen={showEditCategories}
        onClose={() => setShowEditCategories(false)}
        onOperationalMetricsChanged={() => setOperationalMetricsToken((t) => t + 1)}
      />

      {/* Keyed on open, like the category editor above, so the draft layout
          inside starts from what the database currently holds every time it
          is opened rather than from a copy left over from a cancelled edit. */}
      <PnlProjectViewModal
        key={showProjectView ? 'project-view-open' : 'project-view-closed'}
        isOpen={showProjectView}
        onClose={() => setShowProjectView(false)}
        projectId={project.id}
        projectName={project.job_location}
        orgCategories={categories}
        valueRows={[...expenseValues, ...xeroExpenseValues]}
        view={projectPnlView}
        loadError={projectPnlViewError}
        saving={savingProjectPnlView}
        onSave={saveProjectPnlView}
        canClear={canClearPnlValues}
        onValuesChanged={handleClearedOrRestored}
      />

      <PnlEditCellModal
        key={editCell ? `cell-${editCell.category}-${editCell.month}-${editCell.year}` : 'cell-closed'}
        isOpen={!!editCell}
        onClose={() => setEditCell(null)}
        onSave={handleSaveCell}
        categoryName={editCell?.category || ""}
        month={editCell?.month || 1}
        year={editCell?.year || currentYear}
        currentValue={editCell?.value || 0}
        saving={saving}
      />
    </>
  );
}
